import { useEffect, useState } from "react";
import Portal from "../common/Portal";
import { Repeat, X } from "lucide-react";
import api from "../../api";

// A repeating series (backend scheduling/appointment_routes.py, feature
// "recurring"): its visits and an "End series" action, which cancels the
// upcoming visits that haven't started. Paid-online visits are kept and
// listed so staff cancel them one by one (that path refunds).
const STATUS_CLS = {
  Confirmed: "bg-emerald-100 text-emerald-800", Seen: "bg-sky-100 text-sky-800",
  Cancelled: "bg-secondary-100 text-secondary-500 line-through", Declined: "bg-rose-100 text-rose-700",
  "Pending Patient Acceptance": "bg-amber-100 text-amber-800",
};
const fmt = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export default function SeriesModal({ seriesId, onClose, onChanged }) {
  const [series, setSeries] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");

  const load = () => api.getAppointmentSeries(seriesId).then((r) => setSeries(r.data)).catch((e) => setError(e?.response?.data?.error || "Couldn't load this series."));
  useEffect(() => { load(); }, [seriesId]); // eslint-disable-line

  const end = async () => {
    setBusy(true);
    try {
      const r = await api.endAppointmentSeries(seriesId);
      setResult(r.data.message);
      setConfirming(false);
      load();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.response?.data?.error || "Couldn't end the series.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Portal><div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
          <h3 className="flex items-center gap-2 font-bold text-secondary-900"><Repeat className="w-5 h-5 text-primary-600" /> Repeating appointments</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-secondary-400 hover:text-secondary-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <p className="text-sm text-rose-600">{error}</p>}
          {!series && !error && <p className="text-sm text-secondary-500">Loading…</p>}
          {series && (
            <>
              <p className="text-sm text-secondary-700">
                Booked <strong>{series.label}</strong> at <strong>{series.time}</strong>, {series.occurrences} visits asked for, {series.visits.length} booked.
                {series.ended_at && <span className="block mt-1 text-secondary-500">This series has ended.</span>}
              </p>
              <ul className="divide-y divide-secondary-100 rounded-xl border border-secondary-100">
                {series.visits.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className="text-secondary-800">{fmt(v.date)}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[v.status] || "bg-secondary-100 text-secondary-700"}`}>
                      {v.status === "Pending Patient Acceptance" ? "Waiting for patient" : v.status}
                    </span>
                  </li>
                ))}
              </ul>
              {result && <p className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">{result}</p>}
              {!series.ended_at && (confirming ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 space-y-2">
                  <p className="text-sm text-rose-800">Cancel every upcoming visit in this series? Visits already held stay as they are. The patient isn't charged.</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={end} disabled={busy} className="rounded-lg bg-rose-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{busy ? "Ending…" : "Yes, end series"}</button>
                    <button type="button" onClick={() => setConfirming(false)} className="rounded-lg border border-secondary-300 text-sm px-4 py-2">Keep it</button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setConfirming(true)} className="w-full rounded-lg border border-rose-300 text-rose-700 text-sm font-semibold py-2 hover:bg-rose-50">End series</button>
              ))}
            </>
          )}
        </div>
      </div>
    </div></Portal>
  );
}
